import {
  Component,
  ComponentFactoryResolver,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonService } from '../../services/common.service';
import { Tab, TabComponent, TabTypeEnum } from '@iv/model/tabs.model';
import { TabDirective } from '../../directives/tab.directive';
import { FavouritesComponent } from 'app/favourites/components';
import { AppState } from '@iv/store/app.reducer';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { Action, Store } from 'redux';
import * as TabsActions from '../../../store/tabs/tabs.actions';
import { getCurrentTab, getTabs } from '@iv/store/tabs/tabs.selectors';
import { getGroups } from 'app/favourites/redux/favorites.reducer';
import { MainTabPanelComponent } from '../main-tab-panel/main-tab-panel.component';
import Utils from '../../../shared/utils';
import { getColumnsEntities } from '@iv/store/columns/columns.selectors';
import { ColumnsEntities } from '@iv/store/columns/columns.reducer';
import { FuturesMonitorComponent } from 'app/futures-monitor/components/futures-monitor/futures-monitor.component';
import { FutureOptionChainComponent } from 'app/workspace/future-option-chain/future-option-chain.component';
import { OptionChainComponent } from 'app/workspace/option-chain/option-chain.component';
import { AppStateService } from '../../services/app-state.service';
import { getPermissions } from '@iv/store/permissions/permissions.reducer';
import { PermissionTypes } from '@iv/store/permissions/permissions.models';
import { ChartsDashboardComponent } from 'app/charts-dashboard/components/charts-dashboard/charts-dashboard.component';
import { StatisticService } from '@iv/shared/statistic.service';

@Component({
  selector: 'iv-live-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})

export class MainComponent implements OnInit, OnDestroy {

  constructor(
    private commonService: CommonService,
    private componentFactoryResolver: ComponentFactoryResolver,
    @Inject(AppStore) private store: Redux.Store<AppState>,
    private appStateService: AppStateService,
    private readonly statisticService: StatisticService
  ) {
    this.appStateService.initialDataLoaded.subscribe((isLoad: boolean) => {
      if (isLoad) {
        const tabs = getTabs(store.getState());

        if (!tabs.length) {
          this.initTabs(this.appStateService.configInitTab())
            .then(() => {
              this.initMainComponent(store);
              return;
            });
        } else {
          this.addTabsAfterFoundedDifferences();
        }
        this.statisticService.incrementViewTab(this.activeTab.name);

        // TODO: Реализовано для релиза чартов. Добалвяем чарт всегда, если нет хотя бы одного таба чартов.
        if (!tabs.find((tab: Tab) => tab.type === TabTypeEnum.CHART_SERVICE)) {
          this.tabsComponent.createTab(null, 'chartService')
            .then(tab => this.store.dispatch(TabsActions.addTab(tab)));
        }
      }
    });
    this.isMobile = Utils.isMobile();
  }

  @HostBinding('class.mobile') isMobile = false;

  @ViewChild(TabDirective, {static: false})
  private tabHost!: TabDirective;
  @ViewChild(MainTabPanelComponent, {static: false})
  private tabsComponent !: MainTabPanelComponent;

  private get activeTab(): Tab {
    return this._activeTab;
  }

  private set activeTab(value: Tab) {
    this._activeTab = value;
    this.setActiveTab(value);
  }

  public messageText = '';
  public messageType = 'info';
  public messageActive = '';

  private tabs = [];
  private load = true;
  private unsubscribeStore;
  private columnConfigs: ColumnsEntities = {};
  private tabComponents: any[] = [
    {
      name: 'FutureOptionChainComponent',
      component: FutureOptionChainComponent
    },
    {
      name: 'OptionChainComponent',
      component: OptionChainComponent
    },
    {
      name: 'FavouritesComponent',
      component: FavouritesComponent
    },
    {
      name: 'FuturesMonitorComponent',
      component: FuturesMonitorComponent
    },
    {
      name: 'ChartsDashboardComponent',
      component: ChartsDashboardComponent
    }
  ];
  private groups: any = [];
  private _activeTab: Tab = null;

  public ngOnInit(): void {
    this.appStateService.ngOnInit();
    this.commonService.getNotification()
      .subscribe(message => {
        this.notify(message.text, message.type);
      });
  }

  public ngOnDestroy(): void {
    if (typeof this.unsubscribeStore === 'function') {
      this.unsubscribeStore();
    }
  }

  private initMainComponent(store: Store): void {
    this.tabs = getTabs(store.getState());
    this.groups = getGroups(store.getState());
    this.activeTab = this.getActiveTab();
    this.load = false;
    Utils.unmask();
    this.subscribeStore();
  }

  private addTabsAfterFoundedDifferences(): void {
    const oldPermissions = localStorage.getItem('permissions').split(';');
    const actualPermissions = getPermissions(this.store.getState());
    const differencesFound = oldPermissions
      .filter((permission: PermissionTypes) => !actualPermissions.includes(permission))
      .concat(actualPermissions.filter((permission: PermissionTypes) => !oldPermissions.includes(permission)));

    if (differencesFound.length > 0) {
      localStorage.setItem('permissions', actualPermissions.join(';'));

      console.warn('Permission was updated');

      const isStock = !!differencesFound.find(per => per === PermissionTypes.DELAYED_STOCKS || per === PermissionTypes.RT_STOCKS);
      const isFut = !!differencesFound.find(per => per === PermissionTypes.EOD_FUTURES || per === PermissionTypes.RT_FUTURES);

      if (oldPermissions.length < actualPermissions.length) {
        this.initTabs(this.appStateService.configInitTab(isStock, isFut)).then(() => this.initMainComponent(this.store));
      } else {
        this.initMainComponent(this.store);
      }
    } else {
      this.initMainComponent(this.store);
    }
  }

  private subscribeStore(): void {
    this.unsubscribeStore = this.store.subscribe(() => {
      const state = this.store.getState();
      const lastAction: Action = state.lastAction as any;
      const newActiveTab = getCurrentTab(state);
      const newTabs = getTabs(state);
      const newGroups = getGroups(state);
      const configs = getColumnsEntities(state);

      switch (lastAction.type) {
        case '[Tabs] Load Tabs':
        case '[Tabs] Add Tab':
          this.tabs = newTabs;
          this.setNewState();
          break;
        case '[Tabs] Delete tab':
          this.tabs = newTabs;
          this.setNewState();
          setTimeout(() => this.activeTab = newActiveTab);
          return;
        case '[Tabs] Select':
        case '[Tabs] Update tab data':
        case '[Tabs] Update service tab data':
          this.setNewState();
          break;
      }

      if (this.activeTab?.id !== newActiveTab.id) {
        this.activeTab = newActiveTab;
      }

      if (this.appStateService.groupsHasChanges(this.groups, newGroups)) {
        this.groups = newGroups;
        this.setNewState();
      }

      if (this.appStateService.columnsConfigHasChanges(this.columnConfigs, configs)) {
        this.columnConfigs = configs;
        this.setNewState();
      }
    });
  }

  setNewState() {
    if (this.tabs.length > 0) {
      this.commonService.setState(this.tabs, this.columnConfigs).then();
    }
  }

  // TODO: Убрать, когда на беке и для стоков добавят hasOptions, пока что только для фьючей https://jira.egartech.com/browse/IVLIVE-328
  private _instrumentHasOptions(tab: any): Promise<any> {
    return new Promise((resolve) => {
      if (tab.data.instrument && tab.data.instrument.hasOptions === undefined) {
        this.commonService.stockHasOptions(tab.data.instrument.stockId)
          .subscribe((data) => {
            tab.data.instrument = { ...tab.data.instrument, hasOptions: data};
            resolve();
          }, () => {
            tab.data.instrument = { ...tab.data.instrument, hasOptions: true};
            resolve();
          });
      } else {
        resolve();
      }
    });
  }

  /**
   * Добавляем табы при первом заходе в приложение, когда у нас нет пермиссий в сторе и нет никаких табов
   * @param blankTabs - формируем нужные заготовки табов в зависимости от подгруженных с бека пермиссий
   * @private
   */
  private initTabs(blankTabs: { [name: string]: number }): Promise<void> {
    return new Promise((resolve) => {
      const keys = Object.getOwnPropertyNames(blankTabs);
      Promise.all(keys.map((key: string) => this.tabsComponent.createTab(blankTabs[key], key)))
        .then((tabs: Tab[]) => {
          tabs.forEach((tab: Tab) => this.store.dispatch(TabsActions.addTab(tab)));
          resolve();
        });
    });
  }

  private notify(text, type) {
    this.messageText = text;
    this.messageType = type;
    this.messageActive = 'show';
    setTimeout(() => {
      this.messageActive = '';
    }, 7000);
  }

  private setActiveTab(selectedTab: any) {
    this.store.dispatch(TabsActions.selectTab(this.getActiveTab()));
    this._hideChart(false);
    this._instrumentHasOptions(selectedTab).then(() => {
      this.loadComponent(new Tab(this.getComponentByName(selectedTab.component), selectedTab), selectedTab.component);
    });
  }

  private _hideChart(hide) {
    this.commonService.hideChart(hide);
  }

  private getComponentByName(name) {
    if (name === 'WorkspaceComponent') {
      name = 'OptionChainComponent';
    }

    return this.tabComponents.find(tabComponent => {
      return tabComponent.name === name;
    }).component;
  }

  private loadComponent(tab: Tab, name: string) {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(tab.component);
    const viewContainerRef = this.tabHost.viewContainerRef;
    viewContainerRef.clear(); // оставляем только один активный элемент!
    const componentRef = viewContainerRef.createComponent(componentFactory);
    const instance: TabComponent = componentRef.instance as TabComponent;
    if (name === 'FavouritesComponent' || name === 'FuturesMonitorComponent') {
      instance.symbolClickEvent.subscribe(id => {
        this.tabsComponent.openNewTab(id, name === 'FavouritesComponent' ? TabTypeEnum.OPTION_CHAIN : TabTypeEnum.FUTURES_OPTION_CHAIN);
      });
    }
    return componentRef.instance as TabComponent;
  }

  private getActiveTab(): Tab {
    const activeTab = this.tabs.find(tab => {
      return tab.active;
    });

    if (!activeTab) {
      this.tabs[0].active = true;
      return this.tabs[0];
    }

    return activeTab;
  }

}
